import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react'; // Added Loader2 for loading state
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/use-notifications'; // Import the real hook
import { type DbNotification } from '@/server/db/schema'; // Changed to DbNotification
import { useTranslation } from 'react-i18next';

export function NotificationPanel() {
  const { t } = useTranslation();
  const {
    notifications,
    markAllAsRead,
    markAsRead,
    isLoadingList, // Use loading state
    isMarkingRead, // Use mutation loading state
    // Add pagination/infinite scroll later if needed:
    // hasNextPage,
    // fetchNextPage,
    // isFetchingNextPage,
  } = useNotifications(); // Use the real hook

  const formatTimeAgo = (dateInput: Date | string | null): string => {
    if (!dateInput) return '';
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return ''; // Invalid date check

    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    let interval = seconds / 31536000; // years
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = seconds / 2592000; // months
    if (interval > 1) return Math.floor(interval) + "mo ago";
    interval = seconds / 86400; // days
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = seconds / 3600; // hours
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = seconds / 60; // minutes
    if (interval > 1) return Math.floor(interval) + "m ago";
    return Math.floor(seconds) + "s ago";
  };


  return (
    <Card className="border-none shadow-none">
      <CardHeader className="flex flex-row items-center justify-between border-b p-3">
        <CardTitle className="text-base font-semibold">{t('notifications.title')}</CardTitle>
        {/* Check if there are any unread notifications before showing the button */}
        {notifications.some((n) => !n.isRead) && (
          <Button
            variant="link"
            size="sm"
            className="text-xs"
            onClick={(e) => {
              e.preventDefault(); // Prevent popover closing if needed
              markAllAsRead();
            }}
            disabled={isMarkingRead} // Disable while mutation is pending
          >
            {isMarkingRead ? t('notifications.marking') : t('notifications.markAllAsRead')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-72"> {/* Adjust height as needed */}
          {isLoadingList && notifications.length === 0 ? ( // Show loader on initial load
            <div className="flex h-full items-center justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? ( // Show empty state after loading
            <div className="p-4 text-center text-sm text-muted-foreground">
              {t('notifications.allCaughtUp')}
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-3 p-3 hover:bg-accent',
                    !notification.isRead && 'bg-accent/50', // Subtle highlight for unread
                  )}
                  // Add onClick to navigate if link exists, or mark as read
                  onClick={() => {
                    // Only mark as read if not already read and not currently marking
                    if (!notification.isRead && !isMarkingRead) {
                      markAsRead(notification.id);
                    }
                    // Handle navigation (consider closing popover)
                    if (notification.link) {
                      // TODO: Implement navigation logic if needed (e.g., using react-router-dom)
                    }
                  }}
                  style={{
                    cursor: notification.link || !notification.isRead ? 'pointer' : 'default',
                    opacity: isMarkingRead && !notification.isRead ? 0.7 : 1 // Dim if marking
                  }}
                >
                  {/* Optional: Add an icon based on notification type */}
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="text-xs text-muted-foreground">
                        {notification.body}
                      </p>
                    )}
                     <p className="text-xs text-muted-foreground/80">
                      {formatTimeAgo(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <div className="mt-1 h-2 w-2 rounded-full bg-primary" /> // Unread indicator dot
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
      {/* Optional Footer for actions like "View All" or pagination trigger */}
      {/* <CardFooter className="p-3 border-t">
        <Button variant="outline" size="sm" className="w-full">View All Notifications</Button>
      </CardFooter> */}
    </Card>
  );
}